import { HierarchyType } from '../components/mainHierarchyFilter/types/hierarchies.type';

export type FilterType =
	| 'product'
	| 'model'
	| 'subfamily'
	| 'family'
	| 'seller'
	| 'salesZone'
	| 'salesChannel'
	| 'salesForce'
	| 'market'
	| 'customer'
	| 'customerGroup';

export const LEVEL_MAPPING: Record<FilterType, string> = {
	product: 'product',
	model: 'model',
	subfamily: 'subfamily',
	family: 'family',

	seller: 'seller',
	salesZone: 'sales_zone',
	salesChannel: 'sales_channel',
	salesForce: 'sales_force',
	market: 'market',

	customer: 'customer',
	customerGroup: 'customer_group',
};

export const HIERARCHY_MAPPING: Record<FilterType, HierarchyType> = {
	product: 'product',
	model: 'product',
	subfamily: 'product',
	family: 'product',

	seller: 'commercial',
	salesZone: 'commercial',
	salesChannel: 'commercial',
	salesForce: 'commercial',
	market: 'commercial',

	customer: 'customer',
	customerGroup: 'customer',
};

export const PLACEHOLDER_MAPPING: Record<string, string> = {
	product: 'Selecione produtos...',
	model: 'Selecione modelos...',
	subfamily: 'Selecione subfamílias...',
	family: 'Selecione famílias...',

	seller: 'Selecione vendedores...',
	salesZone: 'Selecione zona de vendas...',
	salesChannel: 'Selecione canal de vendas...',
	salesForce: 'Selecione força de vendas...',
	market: 'Selecione mercados...',

	customer: 'Selecione clientes...',
	customerGroup: 'Selecione grupo de clientes...',
};

export const HIERARCHY_EXCLUSION_MAP: Record<string, string[]> = {
	product: ['model', 'subfamily', 'family', 'product'],
	commercial: ['salesZone', 'salesChannel', 'seller', 'salesForce', 'market'],
	customer: ['customerGroup', 'customer'],
};

export interface IHierarchyConfig {
	key: string;
	value: string;
	label: string;
	nameColumn: string;
	hierarchy: HierarchyType;
}

export const HIERARCHY_CONFIGS: IHierarchyConfig[] = [
	{
		key: 'has_market',
		value: 'market',
		label: 'Mercado',
		nameColumn: 'market',
		hierarchy: 'commercial',
	},
	{
		key: 'has_sales_channel',
		value: 'salesChannel',
		label: 'Canal de Venda',
		nameColumn: 'sales_channel',
		hierarchy: 'commercial',
	},
	{
		key: 'has_sales_force',
		value: 'salesForce',
		label: 'Força de Venda',
		nameColumn: 'sales_force',
		hierarchy: 'commercial',
	},
	{
		key: 'has_sales_zone',
		value: 'salesZone',
		label: 'Zona de Venda',
		nameColumn: 'sales_zone',
		hierarchy: 'commercial',
	},
	{
		key: 'has_seller',
		value: 'seller',
		label: 'Vendedor (Membro)',
		nameColumn: 'seller',
		hierarchy: 'commercial',
	},

	{
		key: 'has_customer_group',
		value: 'customerGroup',
		label: 'Grupo de Cliente',
		nameColumn: 'customer_group',
		hierarchy: 'customer',
	},
	{
		key: 'has_customer',
		value: 'customer',
		label: 'Cliente',
		nameColumn: 'customer',
		hierarchy: 'customer',
	},

	{
		key: 'has_family',
		value: 'family',
		label: 'Família',
		nameColumn: 'family',
		hierarchy: 'product',
	},
	{
		key: 'has_subfamily',
		value: 'subfamily',
		label: 'Subfamília',
		nameColumn: 'subfamily',
		hierarchy: 'product',
	},
	{
		key: 'has_model',
		value: 'model',
		label: 'Modelo',
		nameColumn: 'model',
		hierarchy: 'product',
	},
	{
		key: 'has_product',
		value: 'product',
		label: 'Produto (Cód. Produto)',
		nameColumn: 'product',
		hierarchy: 'product',
	},
];

export interface IHierarchySelections {
	[key: string]: any[];
}

export interface IFilterParams {
	level: FilterType;
	hierarchy: HierarchyType;
	cycleId: number;
	search?: string;
	page?: number;
	size?: number;
}

export const getHierarchyForLevel = (level: FilterType): HierarchyType => {
	return HIERARCHY_MAPPING[level];
};

export const getLevelLabel = (level: FilterType) => {
	const config = HIERARCHY_CONFIGS.find(h => h.value === level);
	return config;
};

export const getConfigByLevel = (
	level: FilterType
): IHierarchyConfig | undefined => {
	return HIERARCHY_CONFIGS.find(h => h.value === level);
};

export const getConfigByKey = (key: string): IHierarchyConfig | undefined => {
	return HIERARCHY_CONFIGS.find(h => h.key === key);
};

export const getPlaceholderForLevel = (level: FilterType): string => {
	return PLACEHOLDER_MAPPING[level] || 'Selecione...';
};

export const API_CONSTANTS = {
	DEFAULT_PAGE_SIZE: 20,
	DEFAULT_ORDER_DIRECTION: 'ASC' as const,
	SEARCH_DEBOUNCE_MS: 1500,
};

export const DEFAULT_HIERARCHY_SELECTION: IHierarchySelections = {};

export const DEFAULT_FILTER_PARAMS: Partial<IFilterParams> = {
	page: 1,
	size: API_CONSTANTS.DEFAULT_PAGE_SIZE,
	search: '',
};

export const HIERARCHY_ORDER: Record<HierarchyType, FilterType[]> = {
	product: ['family', 'subfamily', 'model', 'product'],
	commercial: ['market', 'salesChannel', 'salesForce', 'salesZone', 'seller'],
	customer: ['customerGroup', 'customer'],
};

export const getParentLevel = (level: FilterType): FilterType | null => {
	const hierarchy = HIERARCHY_MAPPING[level];
	const order = HIERARCHY_ORDER[hierarchy];
	const idx = order.indexOf(level);
	return idx > 0 ? order[idx - 1] : null;
};

export const getChildrenLevels = (level: FilterType): FilterType[] => {
	const hierarchy = HIERARCHY_MAPPING[level];
	const order = HIERARCHY_ORDER[hierarchy];
	const idx = order.indexOf(level);
	return idx >= 0 ? order.slice(idx + 1) : [];
};
